import { useCallback, useEffect, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { PageCard } from '../CardWithTitle';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { Account } from '../../../types';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import {
  ConfigSource,
  getProjectManagementConfig,
  getRiskDateFields,
  putProjectManagementConfig,
  ProjectManagementConfig,
  ProjectManagementConfigTier,
  RiskDateFieldOption,
  RiskTierName,
  SubjectLineFieldSelection
} from '../../../Api/Admin/ProjectManagementConfigs/projectManagementConfigsApi';
import { extractApiErrorMessage } from '../McpAccess/extractApiErrorMessage';
import {
  changedDates,
  DEFAULT_COMPARISON_DATE_FIELD,
  DEFAULT_PRIMARY_DATE_FIELD,
  hasUnavailableDate,
  measuresOneDateAgainstItself,
  tierDateSides
} from './riskTierDateFields';
import { CatalogUnavailableNote } from './CatalogUnavailableNote';
import { RiskTierNotices } from './RiskTierNotices';
import { RiskTierFormRow, RiskTierRow } from './RiskTierRow';
import { SubjectLinePrefixField } from './SubjectLinePrefixField';
import { useCatalog } from './useCatalog';
import { formatDayThreshold, parseDayThreshold, runsOnPastMax, tierName } from './riskTierPresentation';

const DESCRIPTION =
  'Set day-threshold rules that determine when inventory under a project moves from On Track to Possible Delay ' +
  'or Delayed. Labels and thresholds are configurable per account. Sub-accounts can override parent settings.';

const DEFAULT_SUBJECT_LINE_SELECTION: SubjectLineFieldSelection = { fields: [], source: 'default', inherited: true };

const TIERS_INHERITED_NOTE =
  "Inherited from this account's parent. Saving anything on this card gives this account its own " +
  'copy of all three tiers, which then stops following the parent.';

// Stated in our own words: an API without this endpoint answers "Not Found", which reads as a broken page.
const DATE_CATALOG_ERROR = 'The list of dates could not be loaded, so the dates a tier measures cannot be changed.';
const DATE_CATALOG_EMPTY = 'No dates are available to select.';

// Order-sensitive: the selection is rendered verbatim, so a reorder is a real change.
const sameSelection = (a: string[], b: string[]) => a.length === b.length && a.every((id, index) => id === b[index]);

const toFormRows = (config: ProjectManagementConfig): RiskTierFormRow[] =>
  config.tiers.map((tier) => ({
    tier: tier.tier,
    label: tier.label,
    thresholdText: formatDayThreshold(tier.dayThresholdMin, tier.dayThresholdMax),
    // The card has no direction control for green, and the API only holds green on 'before'.
    direction: tier.tier === 'green' ? 'before' : tier.direction,
    // An API that predates the pair still has to yield an editable row measuring what it measured.
    primaryDateField: tier.primaryDateField?.id ?? DEFAULT_PRIMARY_DATE_FIELD,
    comparisonDateField: tier.comparisonDateField?.id ?? DEFAULT_COMPARISON_DATE_FIELD
  }));

export interface RiskConfigurationFormProps {
  account: Account;
  onClose: () => void;
}

export const RiskConfigurationForm = ({ account, onClose }: RiskConfigurationFormProps) => {
  const { setSnack } = useSnackbar();
  const [rows, setRows] = useState<RiskTierFormRow[]>([]);
  const [storedTiers, setStoredTiers] = useState<ProjectManagementConfigTier[]>([]);
  const [tierSource, setTierSource] = useState<ConfigSource>('default');
  const [subjectFields, setSubjectFields] = useState<string[]>([]);
  const [subjectSource, setSubjectSource] = useState<ConfigSource>('default');
  const [storedSubjectFields, setStoredSubjectFields] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const applyConfig = (config: ProjectManagementConfig) => {
    setRows(toFormRows(config));
    setStoredTiers(config.tiers);
    setTierSource(config.source);
    // An API that predates the selection must still yield an editable tier form rather than a blank card.
    const selection = config.subjectLineFields ?? DEFAULT_SUBJECT_LINE_SELECTION;
    setSubjectFields(selection.fields);
    setStoredSubjectFields(selection.fields);
    setSubjectSource(selection.source);
  };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const { data, error } = await getProjectManagementConfig(account.id);
    if (data) applyConfig(data);
    if (error) setLoadError(extractApiErrorMessage(error, 'Could not load risk configuration'));
    setLoading(false);
  }, [account.id]);

  const fetchDateFields = useCallback(() => getRiskDateFields(account.id), [account.id]);
  const {
    fields: dateFields,
    loading: dateFieldsLoading,
    failed: dateFieldsFailed,
    reload: reloadDateFields
  } = useCatalog<RiskDateFieldOption>(fetchDateFields);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updateRow = (tier: RiskTierName, patch: Partial<RiskTierFormRow>) => {
    setRows((current) => current.map((row) => (row.tier === tier ? { ...row, ...patch } : row)));
  };

  const storedTier = (tier: RiskTierName) => storedTiers.find((stored) => stored.tier === tier);

  // Saving creates this account's rows, so every pair must be named and none may be withdrawn.
  const takingOverInheritedTiers = tierSource !== 'account';

  const unmeasuredTiers = rows
    .filter((row) => hasUnavailableDate(tierDateSides(row, storedTier(row.tier))))
    .map(tierName);

  const selfMeasuringTiers = rows.filter(measuresOneDateAgainstItself).map(tierName);

  const rowValid = (row: RiskTierFormRow) =>
    row.label.trim() !== '' && parseDayThreshold(row.thresholdText) !== null && !measuresOneDateAgainstItself(row);
  const canSave =
    !loading &&
    !saving &&
    rows.length > 0 &&
    rows.every(rowValid) &&
    !(takingOverInheritedTiers && unmeasuredTiers.length > 0);

  const selectionChanged = !sameSelection(subjectFields, storedSubjectFields);

  const handleSave = async () => {
    setSaving(true);
    const tiers = rows.map((row) => {
      const range = parseDayThreshold(row.thresholdText);
      return {
        tier: row.tier,
        label: row.label.trim(),
        day_threshold_min: range?.min ?? null,
        day_threshold_max: range?.max ?? null,
        direction: row.direction,
        ...changedDates(row, storedTier(row.tier), { inherited: takingOverInheritedTiers })
      };
    });
    // Sending an unchanged selection would materialise an inherited one as this account's own override.
    const { data, error } = await putProjectManagementConfig(account.id, tiers, {
      subjectLineFields: selectionChanged ? subjectFields : undefined
    });
    if (data) {
      applyConfig(data);
      setSnack({ message: `Risk configuration saved for ${account.name}`, type: 'success', open: true });
    }
    if (error) {
      setSnack({
        message: extractApiErrorMessage(error, 'Could not save risk configuration'),
        type: 'error',
        open: true
      });
    }
    setSaving(false);
  };

  return (
    <PageCard
      title="Risk Configuration"
      dataCy="risk-config-card"
      enableCollapse={false}>
      {loading && <ComponentSpinner sx={{ marginTop: '2rem', marginBottom: '2rem' }} />}
      {!loading && loadError && (
        <Alert
          severity="error"
          data-testid="risk-config-load-error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={loadConfig}
              data-testid="risk-config-load-retry">
              Retry
            </Button>
          }>
          {loadError}
        </Alert>
      )}
      {!loading && !loadError && (
        <>
          <p className="text-sm text-gray-500 mb-2">{DESCRIPTION}</p>
          {tierSource === 'parent' && (
            <p
              className="text-sm text-gray-500 mb-2"
              data-cy="risk-tiers-inherited-note"
              data-testid="risk-tiers-inherited-note">
              {TIERS_INHERITED_NOTE}
            </p>
          )}
          {rows.map((row) => (
            <RiskTierRow
              key={row.tier}
              row={row}
              dateFields={dateFields}
              sides={tierDateSides(row, storedTier(row.tier))}
              runsOnPastMax={runsOnPastMax(rows, row)}
              onChange={(patch) => updateRow(row.tier, patch)}
            />
          ))}
          {!dateFieldsLoading && dateFields.length === 0 && (
            <CatalogUnavailableNote
              failed={dateFieldsFailed}
              failedMessage={DATE_CATALOG_ERROR}
              emptyMessage={DATE_CATALOG_EMPTY}
              onRetry={reloadDateFields}
              testIdPrefix="risk-date-catalog"
            />
          )}
          <RiskTierNotices
            rows={rows}
            selfMeasuringTiers={selfMeasuringTiers}
            unmeasuredTiers={unmeasuredTiers}
            canReplaceDates={dateFields.length > 0}
            unavailableBlocksSave={takingOverInheritedTiers}
          />
          <SubjectLinePrefixField
            value={subjectFields}
            onChange={setSubjectFields}
            // The API reports `inherited` for an unconfigured account too, so only a parent may claim it.
            showInheritedNote={!selectionChanged && subjectSource === 'parent'}
          />
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-6 pr-8">
            <button
              onClick={onClose}
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              data-cy="risk-config-cancel-btn">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              data-cy="risk-config-save-btn">
              Save
            </button>
          </div>
        </>
      )}
    </PageCard>
  );
};
